import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../utils/Utils.js';
import '../shared/HubLayout.css';
import SubNav from '../layout/SubNav';
import { useMobileSubNav } from '../layout/MobileNavContext';
import { useHubScreen } from '../layout/useHubScreen';
import ListFinances from './ListFinances.js';
import AddFinance from './AddFinance.js';
import FinanceCharts from './FinanceCharts.js';
import ListProjects from './projects/ListProjects.js';
import AddProject from './projects/AddProject.js';
import { currencySymbolFor } from './currency.js';
import Upload from '../shared/Upload.js';
import FinanceCategories, { FinanceCategoriesActions } from './categories/FinanceCategories.js';
import FinanceInstitutions, { FinanceInstitutionsActions } from './institutions/FinanceInstitutions.js';
import { ListButton, AddButton, UploadButton, DownloadButton, FAB } from '../shared/ActionButtons';
import SectionDescription from '../shared/SectionDescription';
import WhatsAppTip from '../shared/WhatsAppTip.js';
import { projectTips } from '../shared/whatsappTips.js';

const SUB_PATHS = ['transactions', 'projects', 'charts', 'categories', 'institutions'];

const SECTION_INFO = [
  { titleKey: 'finance_transactions_section_title', textKey: 'finance_transactions_section_text' },
  { titleKey: 'finance_projects_section_title',     textKey: 'finance_projects_section_text' },
  { titleKey: 'finance_charts_section_title',       textKey: 'finance_charts_section_text' },
  { titleKey: 'finance_categories_section_title',   textKey: 'finance_categories_section_text' },
  { titleKey: 'finance_institutions_section_title', textKey: 'finance_institutions_section_text' },
];

export const FinancesActions = ({ screen, setScreen }) => (
  <div className="action-buttons">
    <ListButton isActive={screen === 'list'} onClick={() => setScreen('list')} />
    <AddButton isActive={screen === 'add'} onClick={() => setScreen('add')} />
    <UploadButton isActive={screen === 'upload'} onClick={() => setScreen('upload')} />
    <DownloadButton collection="finance.transactions" />
  </div>
);

export const ProjectsActions = ({ screen, setScreen }) => (
  <div className="action-buttons">
    <ListButton isActive={screen === 'list'} onClick={() => setScreen('list')} />
    <AddButton isActive={screen === 'add'} onClick={() => setScreen('add')} />
  </div>
);

export const Finances = () => {
  const { t } = useTranslation();
  const { subTab, screen, setScreen } = useHubScreen();
  const navigate = useNavigate();
  const { setSubNav } = useMobileSubNav();

  const activeSubTab = Math.max(0, SUB_PATHS.indexOf(subTab));
  const [currency, setCurrency] = useState('BRL');
  const [projectTip, setProjectTip] = useState(null); // "✅ PROJ-… created" after adding one

  useEffect(() => {
    Fetch('api/data/account.settings', 'GET', null,
      (data) => {
        const settings = Array.isArray(data) ? data[0] : data;
        if (settings?.currency) setCurrency(settings.currency);
      },
      () => {}
    );
  }, []);

  const subTabs = [t('transactions'), t('projects'), t('charts'), t('categories'), t('institutions')];
  const handleSubTabClick = (index) => navigate(`/finances/${SUB_PATHS[index]}`);

  useEffect(() => {
    setSubNav({ tabs: subTabs, activeTab: activeSubTab, onTabClicked: handleSubTabClick });
    return () => setSubNav(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab, setSubNav]);

  const sym = currencySymbolFor(currency);

  return (
    <div className="hub">
      <div className="hub-header">
        <SubNav tabs={subTabs} activeTab={activeSubTab} onTabClicked={handleSubTabClick} />
        <div className="hub-actions">
          {activeSubTab === 0 && <FinancesActions screen={screen} setScreen={setScreen} />}
          {activeSubTab === 1 && <ProjectsActions screen={screen} setScreen={setScreen} />}
          {activeSubTab === 3 && <FinanceCategoriesActions screen={screen} setScreen={setScreen} />}
          {activeSubTab === 4 && <FinanceInstitutionsActions screen={screen} setScreen={setScreen} />}
        </div>
      </div>
      <div className="hub-content">
        <SectionDescription
          title={t(SECTION_INFO[activeSubTab].titleKey)}
          text={t(SECTION_INFO[activeSubTab].textKey)}
        />
        {activeSubTab === 0 && screen === 'list' && (
          <>
            <ListFinances currencySymbol={sym} />
            <FAB onClick={() => setScreen('add')} />
          </>
        )}
        {activeSubTab === 0 && screen === 'add' && (
          <AddFinance onSuccess={() => setScreen('list')} />
        )}
        {activeSubTab === 0 && screen === 'upload' && (
          <Upload collection="finance.transactions" onSuccess={() => setScreen('list')} />
        )}
        {activeSubTab === 1 && screen === 'list' && (
          <>
            {projectTip && (
              <WhatsAppTip title={projectTip.title} tips={projectTip.tips} onDismiss={() => setProjectTip(null)} />
            )}
            <ListProjects currencySymbol={sym} />
            <FAB onClick={() => setScreen('add')} />
          </>
        )}
        {activeSubTab === 1 && screen === 'add' && (
          <AddProject onSuccess={(created) => {
            if (created?.code) setProjectTip({ title: `✅ ${created.code} ${t('created')}`, tips: projectTips(created, t) });
            setScreen('list');
          }} />
        )}
        {activeSubTab === 2 && <FinanceCharts currencySymbol={sym} />}
        {activeSubTab === 3 && <FinanceCategories screen={screen} setScreen={setScreen} hideHeader />}
        {activeSubTab === 4 && <FinanceInstitutions screen={screen} setScreen={setScreen} hideHeader />}
      </div>
    </div>
  );
};

export default Finances;
